import React, { useState, useEffect, useContext, useMemo } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Popper from "@mui/material/Popper";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Tab from "@mui/material/Tab";
import { Link } from "react-router-dom";
import "../../util/getFormattedDate.js";
import getFormattedDate from "../../util/getFormattedDate.js";
import {getFormattedDateOnly, convertSecondsToTAT} from "../../util/getFormattedDate.js";
import "./ProfileCaseListTable.css";
import { caseListPaginationInfo } from "../../constants/constant.js";
import FilterContext from "../StateManagement/Context.js";
import { Input } from "@mui/material";

function isOverflown(element) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
  const { width, value } = props;
  const wrapper = React.useRef(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
        setShowFullCell(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: "center",
        lineHeight: "24px",
        width: 1,
        height: 1,
        position: "relative",
        display: "flex",
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: 1,
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Box>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{ width, marginLeft: -17 }}
        >
          <Paper
            elevation={1}
            style={{ minHeight: wrapper.current.offsetHeight - 3 }}
          >
            <Typography variant="body2" style={{ padding: 8 }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

GridCellExpand.propTypes = {
  value: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
};

function renderCellExpand(params) {
  return (
    <GridCellExpand
      value={params.value || ""}
      width={params.colDef.computedWidth}
    />
  );
}

renderCellExpand.propTypes = {
  colDef: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
};

const columns = [
  {
    field: "case_id",
    headerName: "Case ID",
    flex: 1.3,
    headerAlign: "center",
    editable: false,
    sortable: true,
    renderCell: (params) => {
      return <Link to={`/cases/${params.value}`}> {params.value} </Link>;
    },
  },
  {
    field: "lab_name",
    headerName: "Lab Name",
    flex: 2.5,
    editable: false,
    sortable: true,
    headerAlign: "center",
    renderCell: renderCellExpand,
  },
  {
    field: "state",
    headerName: "Status",
    flex: 1.8,
    editable: false,
    sortable: false,
    headerAlign: "center",
  },
  {
    field: "allocated_to",
    headerName: "Allocated To",
    flex: 1.5,
    editable: false,
    sortable: false,
    headerAlign: "center",
  },
  {
    field: "data_entry_users",
    headerName: "Data Entry User",
    sortable: false,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
  },
  {
    field: "doctorEntryUser",
    headerName: "Doctor Entry User",
    sortable: false,
    editable: false,
    flex: 1.6,
    hideColoumnFor: ["insurance"],
    headerAlign: "center",
  },
  {
    field: "verified_by",
    headerName: "QC User",
    sortable: false,
    editable: false,
    flex: 1.6,
    hideColoumnFor: ["Corporate"],
    headerAlign: "center",
  },
  {
    field: "created_at",
    headerName: "Case Creation",
    sortable: true,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
    renderCell: (params) => {
      return getFormattedDate(params.value);
    },
  },
  {
    field: "lis_json_available",
    headerName: "LIS Available",
    sortable: false,
    editable: false,
    flex: 1,
    headerAlign: "center",
    renderCell: (params) => {
      if (params.value) {
        return "Yes";
      } else {
        return "";
      }
    },
  },
  {
    field: "profile",
    headerName: "Enterprise",
    sortable: true,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
    renderCell: (params) => {
      if (params.row.profile === "Corporate") {
        return params.row.profile_name;
      }
    },
  },
  {
    field: "formType",
    headerName: "MER Type",
    sortable: true,
    editable: false,
    flex: 1.6,
    hideColoumnFor: ["Corporate"],
    headerAlign: "center",
  },
  {
    field: "actualAppointmentDate",
    headerName: "Appointment Date",
    sortable: true,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
    hideColoumnFor: ["insurance"],
    renderCell: (params) => {
      return getFormattedDateOnly(params.value);
    },
  },
  {
    field: "aptDateTAT",
    headerName: "Apt TAT",
    sortable: true,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
    hideColoumnFor: ["Corporate"],
    renderCell: (params) => {
      return convertSecondsToTAT(params.value, "days");
    },
  },
  {
    field: "caseTAT",
    headerName: "Case TAT",
    sortable: true,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
    hideColoumnFor: ["Corporate"],
    renderCell: (params) => {
      return convertSecondsToTAT(params.value, "daysHoursMinutes");
    }
  },
  {
    field: "modified_at",
    headerName: "Updated On",
    sortable: true,
    editable: false,
    flex: 1.6,
    headerAlign: "center",
    renderCell: (params) => {
      return getFormattedDate(params.value);
    },
  },
];

const ProfileCaseListTable = (props) => {
  let {
    data,
    validateRows,
    loading,
    caseListPaginationProperties,
    handlePageChange,
    handlePageSizeChange,
    profileType
  } = props;

  const { caseListFilters } = useContext(FilterContext);
  const [rows, setRows] = useState(data);
  const [updatedRows, setUpdatedRows] = useState(data);
  const [selectionModel, setSelectionModel] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSizeInput, setPageSizeInput] = useState("50");
  const [errorMessage ,setErrorMessage]=useState("");
  useEffect(() => {
    setSelectionModel([]);
    setRows(data);
  }, [data]);

  const HandleRowClicked = (param) => {
    // let history = useHistory()
    //console.log("CaseId: " + param.row.case_id);
    // history.push(`/cases/123`);
  };
  const filteredColoumns = useMemo(() => {
    return  columns.filter((column) => {
       const {hideColoumnFor = true } = column || {};
       if(Array.isArray(hideColoumnFor)) return !hideColoumnFor.includes(profileType);
       else return column;
    })
  },[rows])

  useEffect(() => {
    if (caseListFilters.selectedEnterprise === "All") {
      setUpdatedRows(rows);
    } else {
      setUpdatedRows(
        rows.filter((row) =>
          row.profile === "Corporate"
            ? row.profile_name === caseListFilters.selectedEnterprise
            : row.profile === caseListFilters.selectedEnterprise
        )
      );
    }
  }, [caseListFilters.selectedEnterprise, rows]);

  const selectPage = () => {
    const chosenRows = updatedRows.slice(25 * page, 25 * page + 25);
    validateRows(chosenRows);
    setSelectionModel(chosenRows.map((chosenRows) => chosenRows.id));
  };

  const handlePageSizeInputChange = (event) => {
    const value = event.target.value.trim(); // Remove any leading/trailing whitespace

    if (isNaN(value)) {
      setErrorMessage("Please enter a valid number");
    } else {
      const numberValue = parseInt(value, 10); // Convert value to integer
      if (numberValue < 1 || numberValue > 100) {
        setErrorMessage("Page size must be between 1 and 100");
      } else {
        setPageSizeInput(value);
        setErrorMessage(""); // Clear error message if valid
      }
    }
  };

  const handleApplyPageSize = () => {
 if(pageSizeInput>0){

  handlePageSizeChange(Number(pageSizeInput));
 }
  };
  const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			handleApplyPageSize();
		}
	};
  return (
    <>
      <div className="profile-util " style={{ height: "40rem" }}>
        <div>
          <Button variant="contained" fullWidth={false} onClick={selectPage}>
            Select Page
          </Button>
        </div>
    
        <DataGrid
          rows={rows}
          columns={filteredColoumns}
          pageSize={
            caseListPaginationProperties?.pageSize ??
            caseListPaginationInfo.defaultPageSize
          }
          checkboxSelection
          rowCount={caseListPaginationProperties?.totalRowCount}
          pagination
          paginationMode={caseListPaginationInfo.paginationMode}
          onPageChange={(newPage) => {
            handlePageChange(newPage + 1);
          }}
          selectionModel={selectionModel}
          loading={loading}
          sx={{
            border: 1,
            "& .MuiDataGrid-cell:hover": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-cell": {
              borderRight: 0.5,
              borderTop: 0.5,
              justifyContent: "center",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#3275a8",
              color: "white",
              fontSize: 16,
            },
          }}
          components={{
            Toolbar: GridToolbar,
          }}
          onRowClick={HandleRowClicked}
          onSelectionModelChange={(ids) => {
            const selectedIDs = new Set(ids);
            const selectedRows = updatedRows.filter((row) =>
              selectedIDs.has(row.id)
            );
            validateRows(selectedRows);
            setSelectionModel(ids);
          }}
        />
            <div className="custom-page-size-input">
          <label htmlFor="pageSizeInput">Rows per page: </label>
          <input
            type="text"
            id="pageSizeInput"
            value={pageSizeInput}
            onChange={handlePageSizeInputChange}
            style={{ width: "70px", marginLeft: "10px" }}
            onKeyDown={handleKeyDown}
          />
         <Button
            variant="contained"
            color="primary"
            style={{ marginLeft: "10px" }}
            onClick={handleApplyPageSize}
          >
            Apply
          </Button>
          <p style={{ color: 'red' }}>{errorMessage}</p>
        </div>
      </div>
    </>
  );
};

export default ProfileCaseListTable;
